import { useCallback } from "react";
import { useTranslation } from "react-i18next";

// `\w` admits `_` and the `¿¡«»` range admits `$`, so both are rejected separately below.
// Not a global regex: `/g` makes `.test()` stateful across calls.
const VALID_KEYWORD_CHARACTERS =
  /^([\]\w&+'".Á®ÉÓÜóüÄáŒÖßúäöÛéÀÂâÿŸàèÙçœæÔÆñíÇêëûÈÏéÊïÍîËôÚÎùÑąĄćĆęĘłŁńŃśŚźŹżŻåÅ¿¡«»ĳĲ\s\-\\/-[\u3000-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF00-\uFFEF\s+]*)$/;

const MAX_KEYWORD_LENGTH = 80;

const VALID_ASIN_REGEX = /^[A-Z0-9]{10}$/i;

export function isValidAsin(asin: string): boolean {
  return VALID_ASIN_REGEX.test(asin);
}

export function isValidKeywordCharset(keyword: string): boolean {
  return VALID_KEYWORD_CHARACTERS.test(keyword) && !keyword.includes("$") && !keyword.includes("_");
}

export type KeywordErrorCode = "charset" | "tooLong" | "period" | "hyphen" | "leadingPlus" | "trailingPlus";

export interface KeywordRules {
  allowLeadingPlus?: boolean;
}

export function getKeywordError(
  keyword: string,
  { allowLeadingPlus = false }: KeywordRules = {},
): KeywordErrorCode | null {
  if (!isValidKeywordCharset(keyword)) {
    return "charset";
  }
  if (keyword.length > MAX_KEYWORD_LENGTH) {
    return "tooLong";
  }
  const first = keyword[0];
  const last = keyword[keyword.length - 1];
  if (first === "." || last === ".") {
    return "period";
  }
  if (first === "-" || last === "-") {
    return "hyphen";
  }
  if (first === "+" && !allowLeadingPlus) {
    return "leadingPlus";
  }
  if (last === "+") {
    return "trailingPlus";
  }
  return null;
}

export function getFirstKeywordError(
  keywords: string[] | undefined | null,
  rules?: KeywordRules,
): { keyword: string; code: KeywordErrorCode } | null {
  for (const keyword of keywords ?? []) {
    const code = getKeywordError(keyword, rules);
    if (code) {
      return { keyword, code };
    }
  }
  return null;
}

export function useKeywordErrorMessage() {
  const { t } = useTranslation(["advertising"]);

  return useCallback(
    (code: KeywordErrorCode): string => {
      switch (code) {
        case "tooLong":
          return t(($) => $.advertising["Keywords must be no more than 80 characters in length."]);
        case "period":
          return t(($) => $.advertising["Keywords can't start or end with a period (.)."]);
        case "hyphen":
          return t(($) => $.advertising["Keywords can't start or end with a hyphen (-)."]);
        case "leadingPlus":
          return t(($) => $.advertising["Keywords can't start with an addition sign (+)."]);
        case "trailingPlus":
          return t(($) => $.advertising["Keywords can't end with an addition sign (+)."]);
        case "charset":
          return t(($) => $.advertising["Keywords must contain only letters and/or numbers."]);
      }
    },
    [t],
  );
}
